// src/pages/clinic-system/Appointments.js
import { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Assets
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIconLight from "../../assets/add_icon_light.png";
import infoIcon from "../../assets/information.png";

// Contexts and Utilities
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import useRequest from "../../utils/useRequest";
import { formatDate } from "../../utils/toDate";
import { toMoney } from "../../utils/toMoney";
import useLanguage from "../../utils/useLanguage";

// Components
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import AddAppointmentModal from "./AddAppointmentModal";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import BalanceBoxes from "../../components/BalanceBoxes";
import Dropdown from "../../components/Dropdown";
import EditAppointmentModal from "./EditAppointmentModal";

// Styles
import "../InvoicesManagement.scss";

const Appointments = () => {
  // ==============================
  // 1. Contexts and Translations
  // ==============================
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();
  const {
    loading,
    setLoading,
    searchQuery,
    setSearchQuery,
    searchError,
    searchErrorMessage,
  } = useContext(DataContext);

  // ==============================
  // 2. State Management
  // ==============================

  // Appointments Data
  const [appointments, setAppointments] = useState([]);
  const [totalAmount, setTotalAmount] = useState(null);
  const [appointmentsCurrentPage, setAppointmentsCurrentPage] = useState(1);
  const [appointmentsTotalPages, setAppointmentsTotalPages] = useState(0);
  const [appointmentsTotalRecords, setAppointmentsTotalRecords] = useState(0);
  const [newAppointmentAdded, setNewAppointmentAdded] = useState(false);

  // Doctors Data
  const { data: treatments, refetch: getAllMedicalTreatments } = useRequest(
    "/getAllMedicalTreatments",
    "GET",
    {},
    { organizationId: userData?.organizationId },
    false
  );
  const [allDoctors, setAllDoctors] = useState([{ name: "All", id: "All" }]);
  const doctorAndHealer = ["Doctor", "Healer"];

  const [selectedDoctor, setSelectedDoctor] = useState(
    doctorAndHealer.includes(userData?.role)
      ? {
          id: userData?.id,
          name: userData?.username,
        }
      : {
          id: null,
          name: null,
        }
  );

  // Search and Filters
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("patientName"),
    value: "patientName",
  });
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  // Modals and UI
  const [showInputFields, setShowInputFields] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Appointment Editing
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // ==============================
  // 3. Constants and Configurations
  // ==============================
  const titles = [
    { value: "patientName" },
    { value: "appointmentType" },
    { value: "Date" },
    { value: "time" },
    { value: "price" },
    { value: "healerName" },
    { value: "theOptions", keepOnMobile: true },
  ];

  const searchTitles = useMemo(
    () => [
      { title: t("patientName"), value: "patientName", type: "text" },
      { title: t("price"), value: "price", type: "money" },

      {
        title: t("treatmentType"),
        value: "treatment",
        type: "dropdown",
        dropdown: { data: [] },
      },
    ],
    [t]
  );

  // ==============================
  // 4. API Fetch Functions
  // ==============================
  const getAllDoctors = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId = userData?.organizationId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "organization-Id": organizationId,
          },
          params: {
            page: 1,
            limit: 10,
            search: "",
            // role: "Doctor",
          },
        }
      );

      if (response?.status === 200) {
        const doctors = response.data.users
          .filter((user) => user?.role === "Doctor" || user?.role === "Healer")
          .map((user) => ({
            id: user.id,
            name: user.username || user.email,
            email: user.email,
            role: user.role,
            specialty: user.specialty,
          }));

        setAllDoctors([{ name: "All", id: "All" }, ...doctors]);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  // the new getAll
  const getAllAppointments = async (page = 1, limit = 5, searchTerm = "") => {
    try {
      setLoading(true);
      const token = userData?.token;
      const organizationId = userData?.organizationId;

      if (!token) {
        console.error("Token is missing");
        return;
      }

      // Define all parameters to be sent
      const params = {
        page: page || 1,
        limit: limit,
        organizationId: parseInt(organizationId),
        doctorName: selectedDoctor?.name === "All" ? "" : selectedDoctor?.name,
      };

      // Always include date range if available
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      if (!startDate && !endDate) params.allDates = true;

      // Add search parameters based on selected type
      if (
        selectedTitle?.value === "price" ||
        selectedTitle?.value === "priceAfterDiscount"
      ) {
        params.minPrice = sanitizeNumber(searchQuery);
        params.maxPrice = sanitizeNumber(searchQuery);
      } else if (selectedTitle?.value === "treatment" && searchQuery?.id) {
        params.treatmentName = searchQuery?.name;
      }

      // Keep backward compatibility
      params.searchType = selectedTitle?.value;
      if (selectedTitle?.value !== "date") {
        params.searchQuery =
          selectedTitle.value === "treatment" ? searchQuery?.id : searchTerm;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllAppointments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: params,
        }
      );

      // Rest of the function remains the same
      if (response.status === 200) {
        setTotalAmount(response?.data.aggregations?.priceAfterDiscount);

        const incomingAppointments = response.data.data.map((appointment) => [
          {
            type: TABLE_CELL_TYPES.LINK,
            value: appointment.customerName,
            to: `/patient-profile/${appointment.patientId}`,
          },
          {
            type: TABLE_CELL_TYPES.TEXT,
            value: appointment.treatmentName || "-",
          },
          { type: TABLE_CELL_TYPES.DATE, value: appointment.date },
          { type: TABLE_CELL_TYPES.TEXT, value: appointment.time },
          {
            type: TABLE_CELL_TYPES.MONEY,
            value: appointment.priceAfterDiscount ?? appointment.price ?? 0,
            currency: t("IQD"),
          },
          { type: TABLE_CELL_TYPES.TEXT, value: appointment.doctorName },
          {
            type: TABLE_CELL_TYPES.ELEMENT,
            value: (
              <CustomButton
                variant="blank"
                title={t("ViewInfo")}
                onClick={() => handleViewInfoClick(appointment)}
                icon={infoIcon}
                iconSize={15}
              />
            ),
            keepOnMobile: true,
          },
        ]);

        setAppointments(incomingAppointments);
        setAppointmentsCurrentPage(response?.data?.pagination?.page);
        setAppointmentsTotalPages(response?.data?.pagination?.pages);
        setAppointmentsTotalRecords(response?.data?.pagination?.total);
      }
    } catch (error) {
      console.error(
        "Error fetching appointments:",
        error.response?.data || error
      );
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // 5. Event Handlers
  // ==============================
  const handleSearchType = async (type) => {
    if (type.value === "treatment") {
      getAllMedicalTreatments({ startDate, endDate });
    }
    setSelectedTitle(type);
  };

  const handleSearch = () => {
    let finalSearchQuery;

    if (
      selectedTitle?.value === "price" ||
      selectedTitle?.value === "priceAfterDiscount"
    ) {
      finalSearchQuery = sanitizeNumber(searchQuery);
    } else if (selectedTitle?.value === "treatment") {
      finalSearchQuery = searchQuery?.id;
    } else {
      finalSearchQuery = searchQuery;
    }

    getAllAppointments(appointmentsCurrentPage, 5, finalSearchQuery);
  };

  const handleViewInfoClick = (appointmentData) => {
    setSelectedAppointment(appointmentData);
    setShowInfoModal(true);
  };

  const handleShowInputFields = () => {
    setShowInputFields(!showInputFields);
  };

  // ==============================
  // 6. Side Effects
  // ==============================
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        await getAllDoctors();
        await getAllAppointments(appointmentsCurrentPage);
      } catch (error) {
        console.error("Failed to load initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [newAppointmentAdded, appointmentsCurrentPage, selectedDoctor]);

  // Reset search query
  useEffect(() => {
    return () => setSearchQuery("");
  }, []);

  useEffect(() => {
    if (selectedTitle?.value === "treatment" && treatments?.data) {
      setSelectedTitle((prev) => ({
        ...prev,
        dropdown: {
          ...prev.dropdown,
          data: treatments.data,
        },
      }));
    }
  }, [selectedTitle?.value, treatments]);

  // ==============================
  // 8. Render
  // ==============================

  return (
    <>
      <main className="e-container">
        {/* Back button */}
        {!doctorAndHealer?.includes(userData?.role) && (
          <div>
            <BackToDashboard
              title={t("back")}
              variant="blank"
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={14}
              to={
                userData?.userType === "user"
                  ? "/invoiceManagement"
                  : "/clinic-dashboard"
              }
            />
          </div>
        )}

        <div className="invoice-table-container">
          {/* User info section */}
          <div className="user-section">
            <div className="user">
              <span className="user-text">{`${t("user")}:`}</span>
              <span className="user-text">
                {localStorage.getItem("username")}
              </span>
            </div>
          </div>

          {/* Page title */}
          <div className="under-user-section">
            <div className="page-title">{t("appointments")}</div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {/* Action buttons and filters */}
              <div className="top-action-buttons responsive-box">
                {!doctorAndHealer?.includes(userData?.role) && (
                  <div className="button-group">
                    <CustomButton
                      title={t("addAppointment")}
                      onClick={handleShowInputFields}
                      icon={addIconLight}
                      iconSize={20}
                    />
                  </div>
                )}
                <div>
                  <span>{t("healers")}: </span>
                  <Dropdown
                    options={allDoctors}
                    setSelected={(doctor) => {
                      setSelectedDoctor(doctor);
                    }}
                    selected={{
                      title: selectedDoctor?.name || t("selectHealer"),
                      id: selectedDoctor?.id,
                    }}
                    thick
                  />
                </div>
              </div>

              {/* Search section */}
              <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_400">
                <MultipleSearchFilter
                  options={searchTitles}
                  selected={selectedTitle}
                  setSelected={handleSearchType}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  thick
                  getData={() => {}}
                  handleSearch={handleSearch}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  startDate={startDate}
                  endDate={endDate}
                  flex
                />
              </section>

              {/* Appointments table */}
              {appointments.length > 0 ? (
                <>
                  <RoundedTable titles={titles} rows={appointments} />

                  {/* Pagination */}
                  <section className="pagination">
                    <div className="pagination-buttons">
                      <CustomButton
                        variant="blank"
                        title={t("previous")}
                        onClick={() =>
                          setAppointmentsCurrentPage(
                            appointmentsCurrentPage - 1
                          )
                        }
                        disabled={appointmentsCurrentPage === 1}
                        icon={
                          currentDirection === "ltr" ? previousIcon : nextIcon
                        }
                        iconSize={15}
                      />
                      <span className="pagination-location">
                        {t("page")}: {appointmentsCurrentPage} -{" "}
                        {appointmentsTotalPages}
                      </span>
                      <CustomButton
                        title={t("next")}
                        variant="blank"
                        onClick={() =>
                          setAppointmentsCurrentPage(
                            appointmentsCurrentPage + 1
                          )
                        }
                        disabled={
                          appointmentsCurrentPage === appointmentsTotalPages
                        }
                        icon={
                          currentDirection === "ltr" ? nextIcon : previousIcon
                        }
                        iconSize={15}
                        reverseIcon
                      />
                    </div>

                    <div className="pagination-buttons">
                      <span className="pagination-location">
                        {t("totalRecords")}: {appointmentsTotalRecords}
                      </span>
                    </div>
                  </section>

                  {/* Total amount */}
                  <div>
                    <BalanceBoxes
                      boxes={[
                        {
                          title: "totalAmount",
                          value: `${toMoney(totalAmount)} ${t("IQD")}`,
                        },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <InformationBox>
                  {searchError
                    ? t(searchErrorMessage)
                    : t("noAppointmentFound")}
                </InformationBox>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {showInputFields && (
        <AddAppointmentModal
          handleShowAddAppointment={handleShowInputFields}
          handleNewAppointmentAdded={setNewAppointmentAdded}
          setShowModal={setShowInputFields}
        />
      )}

      {showInfoModal && (
        <EditAppointmentModal
          appointment={selectedAppointment}
          setShowModal={setShowInfoModal}
          allDoctors={allDoctors?.filter((doctor) => doctor.id !== "All")}
          getAllAppointments={getAllAppointments}
        />
      )}
    </>
  );
};

export default Appointments;
